import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { ColorEntry, DrillingSession } from '../models/drilling.models';
import { DrillBlockComponent } from '../drill-block/drill-block.component';
import { hslFromString } from 'src/app/utils/color.utils';

/**
 * Owns the vertical-stacking logic for multiple sessions in one cell.
 * Also renders the blast-date red-dot overlay (§6).
 */
@Component({
  selector: 'drill-block-cell',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './drill-block-cell.component.html',
  styleUrls: ['./drill-block-cell.component.scss'],
  imports: [DrillBlockComponent],
})
export class DrillBlockCellComponent {
  readonly sessions           = input.required<DrillingSession[]>();
  readonly shiftDurationHours = input.required<number>();
  readonly colorBy            = input<'rig' | 'operator'>('rig');
  readonly palette            = input<ColorEntry[]>([]);
  /** When true, draws a 2px red left border and a red filled dot centred in the cell. */
  readonly isBlastDate  = input<boolean>(false);

  readonly sessionClick = output<DrillingSession>();

  /** Sessions sorted by equipmentLabel for deterministic rendering. */
  protected readonly sortedSessions = computed(() =>
    [...this.sessions()].sort((a, b) => a.equipmentLabel.localeCompare(b.equipmentLabel))
  );

  /** Pre-computed color per session (keyed by id) — reacts explicitly to colorBy and palette changes. */
  protected readonly sessionColors = computed(() => {
    const colorBy = this.colorBy();
    const palette = this.palette();
    const result = new Map<string, string>();
    for (const s of this.sessions()) {
      const id    = colorBy === 'rig' ? s.equipmentId : s.operatorId;
      const label = colorBy === 'rig' ? s.equipmentLabel : s.operatorLabel;
      const paletteColor = palette.find(e => e.id === id)?.color;
      result.set(s.id, paletteColor ?? hslFromString(label));
    }
    return result;
  });

  protected getColor(session: DrillingSession): string {
    return this.sessionColors().get(session.id) ?? hslFromString(session.equipmentLabel);
  }
}
